import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Leaf, Info, Zap } from 'lucide-react';
import '../components/AdvancedDashboard.css';
import './pages.css';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { useAuth } from '../auth';
import { LoadingState, EmptyState, ErrorState } from '../components/ui/States';
import { fetchEnvironmentalImpact, fetchEnvironmentalImpactHistory } from '../historyApi';

const PERIODS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'month', label: 'Ce mois' },
  { id: 'year', label: 'Cette année' },
  { id: 'total', label: 'Total' }
];

const ALL_PLANTS_ID = '__all__';

function localDateLabel(dateStr) {
  if (typeof dateStr !== 'string') return dateStr;
  const parts = dateStr.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : dateStr; // day bucket: DD/MM
}

export default function EnvironmentalImpactPage() {
  const { plants } = useOutletContext();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const safePlants = Array.isArray(plants) ? plants : [];

  const [plantId, setPlantId] = useState('');
  const [period, setPeriod] = useState('today');
  const [impact, setImpact] = useState(null); // single-plant result
  const [impactStatus, setImpactStatus] = useState('idle');
  const [allImpacts, setAllImpacts] = useState(null); // array of per-plant results, "Tous les plants" mode
  const [historyData, setHistoryData] = useState(null);
  const [historyStatus, setHistoryStatus] = useState('idle');

  // Auto-select the only plant if there's exactly one; otherwise leave the
  // selector for the user (including the "Tous les plants" option once
  // there's more than one accessible plant).
  useEffect(() => {
    if (!plantId && safePlants.length === 1) setPlantId(safePlants[0].plant_id);
  }, [safePlants, plantId]);

  const isAllPlants = plantId === ALL_PLANTS_ID;

  const loadSingle = useCallback(() => {
    if (!plantId || isAllPlants) return;
    setImpactStatus('loading');
    fetchEnvironmentalImpact(token, plantId, period)
      .then((res) => { setImpact(res); setImpactStatus('ok'); })
      .catch(() => { setImpact(null); setImpactStatus('error'); });
  }, [token, plantId, period, isAllPlants]);

  const loadAll = useCallback(() => {
    if (!isAllPlants) return;
    setImpactStatus('loading');
    Promise.all(safePlants.map((p) =>
      fetchEnvironmentalImpact(token, p.plant_id, period).catch(() => null)
    ))
      .then((results) => { setAllImpacts(results); setImpactStatus('ok'); })
      .catch(() => { setAllImpacts(null); setImpactStatus('error'); });
  }, [token, period, isAllPlants, safePlants]);

  const loadHistory = useCallback(() => {
    if (!plantId || isAllPlants) { setHistoryData(null); setHistoryStatus('idle'); return; }
    setHistoryStatus('loading');
    const histPeriod = period === 'year' || period === 'total' ? 'year' : 'month';
    fetchEnvironmentalImpactHistory(token, plantId, histPeriod)
      .then((res) => { setHistoryData(res); setHistoryStatus('ok'); })
      .catch(() => { setHistoryData(null); setHistoryStatus('error'); });
  }, [token, plantId, period, isAllPlants]);

  useEffect(() => {
    setImpact(null); setAllImpacts(null); setImpactStatus('idle');
  }, [plantId, period]);

  useEffect(() => { if (plantId) { isAllPlants ? loadAll() : loadSingle(); } }, [plantId, period, isAllPlants, loadAll, loadSingle]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Aggregate across accessible plants only - each entry already comes from
  // a per-plant call the backend independently authorized, so summing them
  // client-side never exposes anything the user couldn't already fetch one
  // by one.
  const aggregate = useMemo(() => {
    if (!allImpacts) return null;
    const measured = allImpacts.filter((r) => r && r.data_status === 'measured');
    const withFactor = measured.filter((r) => r.co2_avoided_kg !== null);
    const withCapacity = measured.filter((r) => r.installed_capacity_kw);
    return {
      energyKwh: measured.reduce((s, r) => s + r.energy_produced_kwh, 0),
      co2Kg: withFactor.reduce((s, r) => s + r.co2_avoided_kg, 0),
      capacityKw: withCapacity.reduce((s, r) => s + r.installed_capacity_kw, 0),
      measuredCount: measured.length,
      totalCount: allImpacts.length,
      factorCount: withFactor.length
    };
  }, [allImpacts]);

  const specificYield = aggregate && aggregate.capacityKw > 0 ? aggregate.energyKwh / aggregate.capacityKw : null;

  const chartData = useMemo(() => {
    if (!historyData?.series) return [];
    return historyData.series.map((s) => ({
      date: localDateLabel(s.date),
      'Énergie produite (kWh)': s.energy_kwh,
      'CO₂e évité (kg)': s.co2_avoided_kg
    }));
  }, [historyData]);

  const periodLabel = PERIODS.find((p) => p.id === period)?.label.toLowerCase() || period;

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Impact Environnemental' }]} />
      <div className="page-header">
        <h1><Leaf size={22} /> Impact Environnemental</h1>
      </div>

      {safePlants.length === 0 ? (
        <EmptyState label="Aucune centrale accessible pour votre compte" />
      ) : (
        <>
          <div className="summary-card" style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 200 }}>
              <label>Centrale</label>
              <select value={plantId} onChange={(e) => setPlantId(e.target.value)}>
                {safePlants.length === 0 && <option value="">Aucune</option>}
                {safePlants.length > 1 && <option value={ALL_PLANTS_ID}>Toutes les centrales</option>}
                {safePlants.map((p) => <option key={p.plant_id} value={p.plant_id}>{p.name || p.plant_id}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Période</label>
              <div className="time-range-selector">
                {PERIODS.map((p) => (
                  <button key={p.id} className={period === p.id ? 'active' : ''} onClick={() => setPeriod(p.id)}>{p.label}</button>
                ))}
              </div>
            </div>
          </div>

          {impactStatus === 'loading' && <LoadingState label="Calcul de l'impact environnemental..." />}
          {impactStatus === 'error' && <ErrorState label="Impossible de calculer l'impact environnemental" />}

          {/* SINGLE PLANT */}
          {impactStatus === 'ok' && !isAllPlants && impact && (
            <>
              {impact.data_status === 'no_data' && (
                <EmptyState label={`Impact environnemental non disponible — aucune production mesurée pour cette période (${periodLabel}).`} />
              )}
              {impact.data_status === 'query_error' && (
                <ErrorState label="Impossible de vérifier la production mesurée pour le moment. Réessayez plus tard." />
              )}

              {impact.data_status === 'measured' && (
                <div className="section">
                  <h3 className="section-title">Résumé — {periodLabel}</h3>
                  <div className="summary-grid summary-grid-compact">
                    <div className="summary-card">
                      <div className="summary-label">Énergie solaire produite</div>
                      <div className="summary-value">{impact.energy_produced_kwh.toFixed(1)}<span className="summary-unit"> kWh</span></div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">CO₂e évité</div>
                      <div className="summary-value">
                        {impact.co2_avoided_kg !== null ? impact.co2_avoided_kg.toFixed(1) : 'N/A'}
                        <span className="summary-unit"> kg CO₂e</span>
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Production spécifique</div>
                      <div className="summary-value">
                        {impact.specific_yield_kwh_per_kwp !== null ? impact.specific_yield_kwh_per_kwp.toFixed(1) : 'N/A'}
                        <span className="summary-unit"> kWh/kWp</span>
                      </div>
                    </div>
                    {impact.installed_capacity_kw !== null && (
                      <div className="summary-card">
                        <div className="summary-label">Puissance installée</div>
                        <div className="summary-value">{impact.installed_capacity_kw.toFixed(2)}<span className="summary-unit"> kWp</span></div>
                      </div>
                    )}
                  </div>

                  {!impact.emission_factor.configured && (
                    <div className="summary-sub" style={{ marginTop: 10 }}>
                      Facteur d'émission non configuré pour cette centrale — le CO₂e évité ne peut pas être calculé.
                      {user?.role === 'admin' && (
                        <> <button className="link-button" style={{ display: 'inline', padding: 0 }} onClick={() => navigate('/admin/plants')}>Configurer dans Gestion des Centrales</button></>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ALL PLANTS AGGREGATE */}
          {impactStatus === 'ok' && isAllPlants && aggregate && (
            <div className="section">
              <h3 className="section-title">Résumé agrégé — {periodLabel}</h3>
              {aggregate.measuredCount === 0 ? (
                <EmptyState label={`Impact environnemental non disponible — aucune production mesurée pour cette période (${periodLabel}) sur vos centrales.`} />
              ) : (
                <>
                  <div className="summary-grid summary-grid-compact">
                    <div className="summary-card">
                      <div className="summary-label">Énergie solaire produite</div>
                      <div className="summary-value">{aggregate.energyKwh.toFixed(1)}<span className="summary-unit"> kWh</span></div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">CO₂e évité</div>
                      <div className="summary-value">
                        {aggregate.factorCount > 0 ? aggregate.co2Kg.toFixed(1) : 'N/A'}
                        <span className="summary-unit"> kg CO₂e</span>
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Production spécifique</div>
                      <div className="summary-value">
                        {specificYield !== null ? specificYield.toFixed(1) : 'N/A'}
                        <span className="summary-unit"> kWh/kWp</span>
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Puissance installée</div>
                      <div className="summary-value">{aggregate.capacityKw.toFixed(2)}<span className="summary-unit"> kWp</span></div>
                    </div>
                  </div>
                  <div className="summary-sub" style={{ marginTop: 10 }}>
                    {aggregate.measuredCount} / {aggregate.totalCount} centrale(s) avec production mesurée pour cette période
                    {aggregate.factorCount < aggregate.measuredCount && ` · ${aggregate.factorCount} centrale(s) avec facteur d'émission configuré (CO₂e calculé uniquement pour celles-ci)`}.
                  </div>
                </>
              )}
            </div>
          )}

          {/* HISTORICAL CHART (single plant only) */}
          {!isAllPlants && plantId && (
            <div className="section">
              <h3 className="section-title">Historique</h3>
              {historyStatus === 'loading' && <LoadingState label="Chargement de l'historique..." />}
              {historyStatus === 'error' && <ErrorState label="Impossible de charger l'historique environnemental" />}
              {historyStatus === 'ok' && chartData.length === 0 && (
                <EmptyState label="Aucune donnée historique mesurée pour cette période" />
              )}
              {historyStatus === 'ok' && chartData.length > 0 && (
                <div className="chart-panel">
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorEnergy" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7db3ff" stopOpacity={0.7} /><stop offset="95%" stopColor="#7db3ff" stopOpacity={0} /></linearGradient>
                        <linearGradient id="colorCO2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6fd99a" stopOpacity={0.7} /><stop offset="95%" stopColor="#6fd99a" stopOpacity={0} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                      <XAxis dataKey="date" stroke="#888" tick={{ fill: '#888' }} />
                      <YAxis yAxisId="left" stroke="#888" tick={{ fill: '#888' }} label={{ value: 'kWh', angle: -90, position: 'insideLeft', fill: '#888' }} />
                      <YAxis yAxisId="right" orientation="right" stroke="#888" tick={{ fill: '#888' }} label={{ value: 'kg CO₂e', angle: 90, position: 'insideRight', fill: '#888' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '8px' }} />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="Énergie produite (kWh)" stroke="#7db3ff" fill="url(#colorEnergy)" connectNulls={false} />
                      <Area yAxisId="right" type="monotone" dataKey="CO₂e évité (kg)" stroke="#6fd99a" fill="url(#colorCO2)" connectNulls={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
          {isAllPlants && (
            <div className="summary-sub" style={{ marginBottom: 20 }}>
              Sélectionnez une centrale spécifique pour voir l'historique détaillé.
            </div>
          )}

          {/* METHODOLOGY */}
          <div className="section">
            <details>
              <summary className="section-title" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Info size={16} /> Méthodologie
              </summary>
              <div className="summary-sub" style={{ marginTop: 10, lineHeight: 1.6, maxWidth: 720 }}>
                Le CO₂e évité est estimé à partir de l'énergie solaire réellement mesurée (télémétrie InfluxDB des
                panneaux) et du facteur d'émission réseau configuré pour la centrale. Il ne s'agit pas d'une mesure
                physique directe du CO₂, mais d'une estimation basée sur l'énergie évitée sur le réseau électrique.
                {!isAllPlants && impact?.emission_factor?.configured && (
                  <div style={{ marginTop: 8 }}>
                    <Zap size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Facteur : <strong>{impact.emission_factor.factor_kg_co2e_per_kwh} kg CO₂e/kWh</strong>
                    {impact.emission_factor.source && <> · Source : {impact.emission_factor.source}</>}
                    {impact.emission_factor.year && <> · Année : {impact.emission_factor.year}</>}
                  </div>
                )}
              </div>
            </details>
          </div>
        </>
      )}
    </div>
  );
}
